/** "生成分镜" / Storyboard Generation Wide Card
 * (VISTOERA_STORYBOARD_UNIFIED_VIDEO_ORCHESTRATION_…_P0_V1).
 * Canonical order: Header → Result/Summary → Rough Cut + Shot List
 * (ShotVideoOrchestrationPanel) → Assets → Parameters → Prompt → Footer.
 * Primary Script Context is required (drag / Quick Action); Diverge uses
 * Canonical V3 placeholder siblings (wired in CanvasBoard). */

import {
  ApartmentOutlined,
  CloseOutlined,
  CopyOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Modal, Radio, Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useState, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  STORYBOARD_GENERATION_CAPABILITY,
  videoProductionAssemblyOutputUrl,
  type ImageAssetPickerSelection,
  type StoryboardArtifact,
} from '../../../../api'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import {
  buildExplicitProviderSelectionPatch,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { useNodeDefinition } from '../nodeRegistry'
import {
  computeNeedsRegeneration,
  SCRIPT_CONTEXT_DND_TYPE,
  storyboardGenerateReady,
  type StoryboardScriptContextField,
  type StoryboardSupportingAssetField,
} from '../storyboardGenerationCanvas'
import {
  hasRecoverableSupersededStoryboard,
  isStoryboardRegenerateRunActive,
  type StoryboardRegenerateMode,
} from '../storyboardRegenerateCanvas'
import {
  orphanSupportingAssets,
  resolveStoryboardEntityBindings,
  type StoryboardEntityBinding,
} from '../storyboardEntityBindings'
import {
  applyProductionFormatChange,
  formatMismatchCopy,
  hasGeneratedMediaRequiringFormatConfirm,
  isProductionFormatComplete,
  PRODUCTION_ASPECT_RATIO_OPTIONS,
  PRODUCTION_RESOLUTION_OPTIONS,
  readProductionFormat,
  type ProductionAspectRatio,
  type ProductionResolution,
} from '../storyboardProductionFormat'
import type { FlowNodeData, ScriptContextDragPayload } from '../types'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import {
  isFinalVideoArtifactStale,
  resolveCollapsedStoryboardPoster,
  summarizeShotUnits,
  type CollapsedStoryboardPoster,
  type FinalVideoArtifactSnapshot,
  type ShotUnit,
} from '../videoProductionCanvas'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import ShotVideoOrchestrationPanel from './ShotVideoOrchestrationPanel'

import { NODE_PRESENTATION_SURFACE_CLASS } from '../nodeInteractionContract'
import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_CARD_WIDTH = generationNodeCardWidth('storyboard_generation')
const ADDITIONAL_INSTRUCTIONS_MAX = 500
const ACTIVE_RUN_STATUSES = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])

const REGENERATE_SKELETON_LINES = [
  '正在设计镜头结构…',
  '正在规划镜头节奏…',
  '正在整理人物与场景连续性…',
] as const

const SHOT_COUNT_PRESETS = [
  { key: 'AUTO', label: 'AUTO' },
  { key: '6', label: '6', value: 6 },
  { key: '8', label: '8', value: 8 },
  { key: '10', label: '10', value: 10 },
] as const

const DURATION_PRESETS = [
  { key: 'AUTO', label: 'AUTO' },
  { key: '15', label: '15秒', value: 15 },
  { key: '30', label: '30秒', value: 30 },
  { key: '60', label: '60秒', value: 60 },
] as const

const STYLE_OPTIONS = ['AUTO', '电影质感', '商业广告', '纪录片', '短视频', '自定义']
const CAMERA_LANGUAGE_OPTIONS = ['AUTO', '现代商业片', '电影叙事', '快速剪辑', '稳定长镜头', '自定义']

function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || seconds <= 0) return null
  if (seconds < 60) return `${Math.round(seconds)}秒`
  const minutes = seconds / 60
  return `${minutes % 1 === 0 ? minutes : minutes.toFixed(1)}分钟`
}

function formatDurationLabel(seconds: number | null | undefined): string {
  return formatDuration(seconds) || '0秒'
}

/** Compact static result preview — derived poster, never a full player. */
function CollapsedStoryboardVideoPoster({ poster }: { poster: CollapsedStoryboardPoster }) {
  const [useVideoFallback, setUseVideoFallback] = useState(!poster.posterUrl && Boolean(poster.videoUrl))
  useEffect(() => {
    setUseVideoFallback(!poster.posterUrl && Boolean(poster.videoUrl))
  }, [poster.posterUrl, poster.videoUrl, poster.shot?.shotId, poster.kind])

  return (
    <div
      className="pcv-storyboard-collapsed-poster"
      data-testid="storyboard-collapsed-video-poster"
      data-poster-kind={poster.kind}
      data-poster-shot-id={poster.shot?.shotId || ''}
      data-poster-shot-index={poster.shotIndexLabel ?? ''}
    >
      {!useVideoFallback && poster.posterUrl ? (
        <img
          src={poster.posterUrl}
          alt=""
          className="pcv-storyboard-collapsed-poster-media"
          onError={() => {
            if (poster.videoUrl) setUseVideoFallback(true)
          }}
        />
      ) : poster.videoUrl ? (
        <video
          className="pcv-storyboard-collapsed-poster-media"
          src={poster.videoUrl}
          muted
          playsInline
          preload="metadata"
          controls={false}
        />
      ) : null}
      <div className="pcv-storyboard-collapsed-poster-caption">
        {poster.kind === 'final_video'
          ? '最终合并视频'
          : `#${poster.shotIndexLabel} · ${poster.timeRangeLabel || ''}`}
      </div>
    </div>
  )
}

function SupportingAssetChip({
  asset,
  workspaceId,
  onRemove,
}: {
  asset: StoryboardSupportingAssetField
  workspaceId: string
  onRemove: () => void
}) {
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    if (asset.picker_item_id && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, asset.picker_item_id, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [asset.picker_item_id, workspaceId])
  return (
    <span className="pcv-brief-asset-chip nodrag" title={asset.display_name}>
      <ImageThumbnailHoverPreview thumbUrl={thumb} />
      <span className="pcv-brief-asset-name">{asset.display_name}</span>
      {asset.inherited ? <span className="pcv-script-asset-inherited-dot" title="继承自剧本" /> : null}
      <button type="button" className="pcv-brief-asset-remove" aria-label="移除" onClick={onRemove}>
        <CloseOutlined />
      </button>
    </span>
  )
}

function EntityBindingCard({
  entity,
  workspaceId,
}: {
  entity: StoryboardEntityBinding
  workspaceId: string
}) {
  const [thumb, setThumb] = useState<string | null>(null)
  const ref = entity.primary_reference
  useEffect(() => {
    let cancelled = false
    if (ref?.picker_item_id && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, ref.picker_item_id, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [ref?.picker_item_id, workspaceId])
  return (
    <div
      className={`pcv-storyboard-entity-card ${NODE_PRESENTATION_SURFACE_CLASS}`}
      data-testid="storyboard-entity-binding"
      data-entity-kind={entity.kind}
      data-entity-id={entity.entity_id}
      data-entity-name={entity.name}
    >
      <div className="pcv-storyboard-entity-card-head">
        <span className="pcv-storyboard-entity-kind">{entity.kind === 'character' ? '角色' : '产品'}</span>
        <span className="pcv-storyboard-entity-name">{entity.name}</span>
      </div>
      {entity.kind === 'character' ? (
        <div className="pcv-storyboard-entity-ref">
          {thumb ? <ImageThumbnailHoverPreview thumbUrl={thumb} /> : null}
          <span>
            参考形象：{ref?.display_name || '未绑定参考图'}
            {!entity.character_card_asset ? (
              <span className="pcv-storyboard-entity-card-optional">（角色卡可选）</span>
            ) : null}
          </span>
        </div>
      ) : (
        <div className="pcv-storyboard-entity-ref">
          {thumb ? <ImageThumbnailHoverPreview thumbUrl={thumb} /> : null}
          <span>
            {entity.visual_source_label
              ? `来源：${entity.visual_source_label}`
              : ref
                ? `参考：${ref.display_name}`
                : '未绑定视觉来源'}
          </span>
        </div>
      )}
    </div>
  )
}

function HistoryList({
  entries,
}: {
  entries: Array<{ storyboard: StoryboardArtifact; version: number; completedAt?: string }>
}) {
  if (entries.length === 0) {
    return <div className="pcv-gen-history-empty">暂无历史版本</div>
  }
  return (
    <div className="pcv-gen-history-list">
      {entries.map((h, idx) => (
        <div key={`${h.version}-${idx}`} className="pcv-gen-history-item">
          <span className="pcv-gen-history-version">V{h.version}</span>
          <div className="pcv-gen-history-body">
            <div className="pcv-gen-history-title">{h.storyboard?.title || '分镜'}</div>
            <div className="pcv-gen-history-meta">
              <span>{h.storyboard?.shots?.length || 0} 镜</span>
              <span>{formatDuration(h.storyboard?.total_duration_sec) || '—'}</span>
              <span>{h.completedAt ? new Date(h.completedAt).toLocaleString() : ''}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function StoryboardGenerationNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const scriptContext = (fields.scriptContext as StoryboardScriptContextField | null) ?? null
  const supportingAssets = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as StoryboardSupportingAssetField[])
    : []
  const shotCountMode = String(fields.shotCountMode ?? 'AUTO')
  const shotCount = typeof fields.shotCount === 'number' ? (fields.shotCount as number) : null
  const durationMode = String(fields.durationMode ?? 'AUTO')
  const totalDurationSec = typeof fields.totalDurationSec === 'number' ? (fields.totalDurationSec as number) : null
  const styleReference = String(fields.styleReference ?? 'AUTO')
  const cameraLanguage = String(fields.cameraLanguage ?? 'AUTO')
  const productionFormat = useMemo(() => readProductionFormat(fields), [fields])
  const additionalInstructions = String(fields.additionalInstructions ?? '')
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null) ?? null
  const storyboardResult = (fields.storyboardResult as StoryboardArtifact | null) ?? null
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as Array<{ storyboard: StoryboardArtifact; version: number; completedAt?: string }>)
    : []
  const runStatus = (fields.runStatus as string | null) ?? null
  const runErrorMessage = (fields.runErrorMessage as string | null) ?? null
  const divergeRunStatus = (fields.divergeRunStatus as string | null) ?? null
  const divergeRunErrorMessage = (fields.divergeRunErrorMessage as string | null) ?? null
  const finalVideoArtifact = (fields.finalVideoArtifact as FinalVideoArtifactSnapshot | null) ?? null
  const assemblyRunStatus = (fields.assemblyRunStatus as string | null) ?? null
  const assemblyErrorMessage = (fields.assemblyErrorMessage as string | null) ?? null

  const [dragOver, setDragOver] = useState<'accept' | 'reject' | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [customShotDraft, setCustomShotDraft] = useState('')
  const [customDurationDraft, setCustomDurationDraft] = useState('')
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false)
  const [regenerateMode, setRegenerateMode] = useState<StoryboardRegenerateMode>('keep_video_nodes')

  const units: ShotUnit[] = useMemo(() => nodeData.videoProductionUnits ?? [], [nodeData.videoProductionUnits])
  const summary = useMemo(() => summarizeShotUnits(units), [units])
  const stale = useMemo(
    () =>
      Boolean(fields.finalVideoFormatMismatch) ||
      (finalVideoArtifact ? isFinalVideoArtifactStale(finalVideoArtifact, units) : false),
    [finalVideoArtifact, units, fields.finalVideoFormatMismatch],
  )

  const collapsedPoster = useMemo(
    () =>
      resolveCollapsedStoryboardPoster({
        units,
        finalVideoArtifact,
        finalVideoStale: stale,
        finalVideoUrl:
          finalVideoArtifact && workspaceId
            ? videoProductionAssemblyOutputUrl(workspaceId, finalVideoArtifact.assemblyId)
            : null,
      }),
    [units, finalVideoArtifact, stale, workspaceId],
  )
  const totalDurationForSummary = useMemo(() => {
    const canonical = storyboardResult?.total_duration_sec
    if (typeof canonical === 'number' && canonical > 0) return canonical
    if (units.length === 0) return 0
    return units[units.length - 1]?.timelineEndSec ?? 0
  }, [storyboardResult, units])
  const incompleteShotVideoCount = Math.max(0, summary.total - summary.generatedCount)

  const entityBindings = useMemo(
    () =>
      resolveStoryboardEntityBindings({
        script: scriptContext?.script ?? null,
        supportingAssets,
      }),
    [scriptContext?.script, supportingAssets],
  )
  const looseAssets = useMemo(
    () => orphanSupportingAssets(supportingAssets, entityBindings) as StoryboardSupportingAssetField[],
    [supportingAssets, entityBindings],
  )
  const formatLocked = useMemo(
    () =>
      hasGeneratedMediaRequiringFormatConfirm({
        generatedShotVideoCount: summary.generatedCount,
        hasFinalVideo: Boolean(finalVideoArtifact) && !fields.finalVideoFormatMismatch,
      }) || units.some((u) => u.status === 'FORMAT_MISMATCH'),
    [summary.generatedCount, finalVideoArtifact, fields.finalVideoFormatMismatch, units],
  )
  const { options: providerOptions, error: providerOptionsError } = useCapabilityProviderOptions(
    workspaceId,
    STORYBOARD_GENERATION_CAPABILITY,
    'storyboard_generation',
  )
  const resolvedProviderOptionId = useMemo(
    () =>
      resolveCapabilityProviderOptionId({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
      }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => {
        const available = isProviderOptionSelectable(opt)
        const base = opt.frontend_display_name
        return {
          value: opt.provider_option_id,
          disabled: !available,
          label: available ? base : `${base}${t('generation.engineUnavailableSuffix')}`,
          title: opt.frontend_description || base,
        }
      }),
    [providerOptions, t],
  )

  const isGenerating = ACTIVE_RUN_STATUSES.has(runStatus || '')
  const isDiverging = ACTIVE_RUN_STATUSES.has(divergeRunStatus || '')
  const isBusy = isGenerating || isDiverging
  const isRegenerating = isStoryboardRegenerateRunActive(fields)
  const canRecoverSuperseded = runStatus === 'ERROR' && hasRecoverableSupersededStoryboard(fields)
  const visibleStoryboardResult = isRegenerating ? null : storyboardResult
  const paramsLocked = isBusy
  const needsRegeneration = computeNeedsRegeneration(fields, nodeData.scriptContextLiveFingerprint)

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const onDragOverContext = (e: ReactDragEvent) => {
    if (!e.dataTransfer.types.includes(SCRIPT_CONTEXT_DND_TYPE)) {
      setDragOver('reject')
      return
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDragOver('accept')
  }
  const onDragLeaveContext = () => setDragOver(null)
  const onDropContext = (e: ReactDragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(null)
    const raw = e.dataTransfer.getData(SCRIPT_CONTEXT_DND_TYPE)
    if (!raw) {
      message.error('生成分镜需要「生成剧本」结果作为剧本上下文。')
      return
    }
    try {
      const payload = JSON.parse(raw) as ScriptContextDragPayload
      nodeData.onLinkScriptContext?.(id, payload)
    } catch {
      message.error('生成分镜需要「生成剧本」结果作为剧本上下文。')
    }
  }

  const openSupportingAssetPicker = () => {
    if (paramsLocked) return
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: '添加关联资产',
      selectionMode: 'multiple',
      maxSelection: 20,
      initialSelection: supportingAssets
        .map((a) => a.picker_item_id || (a.asset_id ? `sa:${a.asset_id}` : ''))
        .filter(Boolean),
      onConfirm: (selections: ImageAssetPickerSelection[]) => {
        const next: StoryboardSupportingAssetField[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          asset_type: 'image_asset',
          display_name: s.filename || s.assetId,
          inherited: false,
        }))
        patchFields({ supportingAssets: next })
      },
    })
  }
  const removeSupportingAsset = (asset: StoryboardSupportingAssetField) => {
    const key = asset.asset_id || asset.display_name
    patchFields({ supportingAssets: supportingAssets.filter((a) => (a.asset_id || a.display_name) !== key) })
  }

  const pickShotCount = (key: string, value?: number) => {
    if (paramsLocked) return
    if (key === 'AUTO') {
      patchFields({ shotCountMode: 'AUTO', shotCount: null })
      return
    }
    patchFields({ shotCountMode: 'EXPLICIT', shotCount: value ?? null })
  }
  const commitCustomShotCount = () => {
    if (paramsLocked) return
    const n = Number(customShotDraft)
    if (Number.isFinite(n) && n > 0) {
      patchFields({ shotCountMode: 'CUSTOM', shotCount: Math.round(n) })
    }
    setCustomShotDraft('')
  }
  const pickDuration = (key: string, value?: number) => {
    if (paramsLocked) return
    if (key === 'AUTO') {
      patchFields({ durationMode: 'AUTO', totalDurationSec: null })
      return
    }
    patchFields({ durationMode: 'EXPLICIT', totalDurationSec: value ?? null })
  }
  const commitCustomDuration = () => {
    if (paramsLocked) return
    const n = Number(customDurationDraft)
    if (Number.isFinite(n) && n > 0) {
      patchFields({ durationMode: 'CUSTOM', totalDurationSec: Math.round(n) })
    }
    setCustomDurationDraft('')
  }

  const applyFormatPatch = useCallback(
    (patch: { aspect_ratio?: ProductionAspectRatio; resolution?: ProductionResolution }) => {
      if (paramsLocked) return
      const { next, versionBumped, impact } = applyProductionFormatChange(productionFormat, patch)
      const needsConfirm =
        versionBumped &&
        hasGeneratedMediaRequiringFormatConfirm({
          generatedShotVideoCount: summary.generatedCount,
          hasFinalVideo: Boolean(finalVideoArtifact),
          hasGeneratedKeyframes: Boolean(nodeData.hasStoryboardKeyframeOutputs),
        })
      const commit = () => {
        if (needsConfirm || versionBumped) {
          nodeData.onInvalidateStoryboardProductionFormat?.(id, next, impact)
        } else {
          patchFields({
            productionFormat: next,
            aspectRatio: next.aspect_ratio,
            finalVideoFormatMismatch: false,
          })
        }
      }
      if (!needsConfirm) {
        commit()
        return
      }
      const copy = formatMismatchCopy()
      Modal.confirm({
        title: copy.title,
        content: copy.content,
        okText: copy.okText,
        cancelText: copy.cancelText,
        onOk: () => commit(),
      })
    },
    [productionFormat, summary.generatedCount, finalVideoArtifact, nodeData, id, paramsLocked, patchFields],
  )

  const canGenerate = storyboardGenerateReady(fields) && !isBusy
  const formatBlockingHint = !isProductionFormatComplete(productionFormat) ? '请先选择成片规格（长宽比与清晰度）' : null
  const canDiverge = !!scriptContext && !isBusy
  const canDuplicate = !isBusy
  // P0: delete allowed while generating — background run continues; success → asset + bill.

  const handleDuplicate = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!canDuplicate) return
    nodeData.onDuplicateNode?.(id)
  }
  const handleDelete = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const handleResultExpandClick = () => {
    nodeData.onExpandFunctionCardDetail?.(id)
  }

  const openShotGenerator = useCallback(
    (shotId: string) => {
      nodeData.onOpenOrCreateShotVideoNode?.(id, shotId)
    },
    [nodeData, id],
  )

  const handleRunAssembly = useCallback(() => {
    nodeData.onRunVideoProductionAssembly?.(id)
  }, [nodeData, id])

  const handleGenerateClick = useCallback(() => {
    if (!storyboardResult) {
      if (canRecoverSuperseded) {
        nodeData.onGenerateStoryboard?.(id, { regenerateMode })
        return
      }
      nodeData.onGenerateStoryboard?.(id)
      return
    }
    setRegenerateMode('keep_video_nodes')
    setRegenerateConfirmOpen(true)
  }, [canRecoverSuperseded, id, nodeData, regenerateMode, storyboardResult])

  const confirmRegenerate = useCallback(() => {
    setRegenerateConfirmOpen(false)
    nodeData.onGenerateStoryboard?.(id, { regenerateMode })
  }, [id, nodeData, regenerateMode])

  const handleRestoreSuperseded = useCallback(() => {
    nodeData.onRestoreSupersededStoryboard?.(id)
  }, [id, nodeData])

  const generateStatusLabel = useMemo(() => {
    const status = isDiverging ? divergeRunStatus : runStatus
    if (status === 'SUBMITTING') return isRegenerating ? '正在提交重新生成…' : '正在提交…'
    if (status === 'QUEUED') return isRegenerating ? '排队重新生成…' : '排队中…'
    if (status === 'GENERATING') return isRegenerating ? '正在重新生成分镜…' : '正在生成分镜…'
    return null
  }, [divergeRunStatus, isDiverging, isRegenerating, runStatus])

  const shots = visibleStoryboardResult?.shots || []
  const isCustomShotActive =
    (shotCountMode === 'CUSTOM' || shotCountMode === 'EXPLICIT') &&
    shotCount != null &&
    !SHOT_COUNT_PRESETS.some((p) => 'value' in p && p.value === shotCount)
  const isCustomDurationActive =
    (durationMode === 'CUSTOM' || durationMode === 'EXPLICIT') &&
    totalDurationSec != null &&
    !DURATION_PRESETS.some((p) => 'value' in p && p.value === totalDurationSec)

  const cardWidth = NODE_CARD_WIDTH
  const mergeStatusLabel = finalVideoArtifact ? (stale ? '合并结果已过期' : '最终已合并') : '预览·未合并'

  const orchestrationSummaryBand =
    visibleStoryboardResult ? (
      <div className="pcv-vp-upstream nodrag" data-testid="storyboard-orchestration-summary">
        <span className="pcv-vp-upstream-title" title={visibleStoryboardResult.title || '分镜'}>
          {visibleStoryboardResult.title || '分镜结果'}
        </span>
        <span className="pcv-vp-upstream-meta">
          {summary.total || shots.length}个镜头 · {formatDurationLabel(totalDurationForSummary)}
        </span>
        <span className="pcv-vp-status-pill">
          {summary.generatedCount}/{summary.total || shots.length}已生成
        </span>
        <span
          className={`pcv-vp-status-pill${
            finalVideoArtifact && !stale ? ' pcv-vp-status-pill--ready' : stale ? ' pcv-vp-status-pill--stale' : ''
          }`}
        >
          {mergeStatusLabel}
        </span>
      </div>
    ) : null

  return (
    <div
      className={`pcv-node pcv-node--storyboard-generation${showExpanded ? ' pcv-node--unified-orchestrator' : ''}${selected ? ' is-selected' : ''}${dragOver === 'accept' ? ' is-drop-accept' : ''}${dragOver === 'reject' ? ' is-drop-reject' : ''} ${canvasNodeShellStateClass(showExpanded)}`}
      style={{ width: cardWidth }}
      data-testid="canvas-storyboard-generation-node"
      onDragOver={onDragOverContext}
      onDragLeave={onDragLeaveContext}
      onDrop={onDropContext}
    >
      <Handle type="target" position={Position.Left} id="storyboard-generation-in" className="pcv-handle" />
      <Handle type="source" position={Position.Right} id="storyboard-generation-out" className="pcv-handle" />

      {/* ① Header — icon + title only; RIGHT History|Duplicate|Delete */}
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={def.label}
        actions={
          <>
            <Tooltip title="生成历史" mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" onClick={() => setHistoryOpen((v) => !v)}>
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={isBusy ? '正在生成，完成后可以复制' : '复制节点'} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" disabled={!canDuplicate} onClick={handleDuplicate}>
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={'删除节点'} mouseEnterDelay={0.3}>
              <button
                type="button"
                className="pcv-media-hover-btn pcv-media-hover-btn--danger"
                onClick={handleDelete}
              >
                <CloseOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {historyOpen ? (
        <div className="pcv-storyboard-history-panel nodrag" onClick={(e) => e.stopPropagation()}>
          <HistoryList entries={generationHistory} />
        </div>
      ) : null}

      {/* ② Upstream Script Context — BODY input band (not Header) */}
      <div className={`pcv-storyboard-upstream ${NODE_PRESENTATION_SURFACE_CLASS}`} data-testid="storyboard-upstream-context">
        <span className="pcv-storyboard-upstream-label">剧本上下文</span>
        {scriptContext ? (
          <span className="pcv-storyboard-script-chip" title={scriptContext.script?.title || '剧本上下文'}>
            {scriptContext.script?.title || '已关联剧本'}
            <span className="pcv-storyboard-upstream-from"> · 来自生成剧本</span>
          </span>
        ) : (
          <span className="pcv-storyboard-upstream-empty" data-testid="storyboard-upstream-empty">
            未连接 · 从「生成剧本」快捷动作创建，或拖入剧本结果
          </span>
        )}
      </div>

      {/* ③ Result — compact empty / collapsed summary */}
      <div
        className={`pcv-gen-result-area pcv-storyboard-result-area pcv-storyboard-result-area--compact ${NODE_EXPAND_TRIGGER_CLASS}`}
        onClick={handleResultExpandClick}
      >
        {isRegenerating ? (
          <div className="pcv-storyboard-regenerate-loading" data-testid="storyboard-regenerate-generating">
            <LoadingOutlined spin />
            <div className="pcv-storyboard-regenerate-title">正在重新生成分镜…</div>
            <div className="pcv-gen-result-empty-hint">
              正在根据当前剧本、关联资产和生成参数重新设计镜头结构。
            </div>
            <div className="pcv-storyboard-regenerate-skeleton">
              {REGENERATE_SKELETON_LINES.map((line) => (
                <div key={line} className="pcv-storyboard-regenerate-skeleton-row">
                  <span className="pcv-storyboard-regenerate-skeleton-index"># —</span>
                  <span className="pcv-storyboard-regenerate-skeleton-text">{line}</span>
                </div>
              ))}
            </div>
          </div>
        ) : !visibleStoryboardResult ? (
          <div className="pcv-gen-result-empty pcv-storyboard-result-empty--compact">
            <div className="pcv-script-section-label" style={{ alignSelf: 'flex-start', width: '100%' }}>
              生成结果 <span className="pcv-brief-header-badge">未生成</span>
            </div>
            <span>暂无分镜结果</span>
            <span className="pcv-gen-result-empty-hint">
              {scriptContext ? '配置参数后点击「生成分镜」' : '连接剧本后即可生成'}
            </span>
          </div>
        ) : (
          <div className={`pcv-storyboard-result-card ${NODE_PRESENTATION_SURFACE_CLASS}`}>
            {needsRegeneration ? (
              <div className="pcv-candidate-stale-banner">剧本或参数已更新，需要重新生成分镜</div>
            ) : null}
            {!showExpanded ? orchestrationSummaryBand : null}
            {!showExpanded && collapsedPoster.kind !== 'none' ? (
              <CollapsedStoryboardVideoPoster poster={collapsedPoster} />
            ) : null}
            {!showExpanded && visibleStoryboardResult && collapsedPoster.kind === 'none' && summary.generatedCount === 0 ? (
              <div className="pcv-storyboard-collapsed-empty-video" data-testid="storyboard-collapsed-no-video">
                暂无分镜视频
              </div>
            ) : null}
            {showExpanded ? (
              <div className="pcv-candidate-meta-row">
                {productionFormat && isProductionFormatComplete(productionFormat) ? (
                  <span className="pcv-candidate-tag" data-testid="storyboard-result-format-tag">
                    {productionFormat.aspect_ratio} · {productionFormat.resolution}
                  </span>
                ) : visibleStoryboardResult.aspect_ratio ? (
                  <span className="pcv-candidate-tag">{visibleStoryboardResult.aspect_ratio}</span>
                ) : null}
                {visibleStoryboardResult.style ? (
                  <span className="pcv-candidate-tag">{visibleStoryboardResult.style}</span>
                ) : null}
              </div>
            ) : null}
            {visibleStoryboardResult.synopsis && showExpanded ? (
              <div className="pcv-storyboard-result-synopsis">{visibleStoryboardResult.synopsis}</div>
            ) : null}
          </div>
        )}
        {isBusy ? (
          <div className="pcv-storyboard-generating-indicator">
            <LoadingOutlined spin />
            <span>{generateStatusLabel}</span>
          </div>
        ) : null}
        {!isDiverging && runStatus === 'ERROR' && runErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="storyboard-regenerate-error">
            {isRegenerating || canRecoverSuperseded ? '重新生成分镜失败' : runErrorMessage}
            {canRecoverSuperseded ? (
              <div className="pcv-storyboard-regenerate-recover-actions">
                <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={handleGenerateClick}>
                  重试
                </button>
                <button
                  type="button"
                  className="pcv-brief-asset-add-btn nodrag"
                  data-testid="storyboard-restore-superseded-btn"
                  onClick={handleRestoreSuperseded}
                >
                  恢复上一个分镜
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {divergeRunStatus === 'ERROR' && divergeRunErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">发散失败：{divergeRunErrorMessage}</div>
        ) : null}
      </div>

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()}>
          {visibleStoryboardResult ? (
            <>
              {orchestrationSummaryBand}
              <ShotVideoOrchestrationPanel
                units={units}
                workspaceId={workspaceId}
                canonicalTotalDurationSec={visibleStoryboardResult.total_duration_sec}
                finalVideoArtifact={finalVideoArtifact}
                assemblyRunStatus={assemblyRunStatus}
                assemblyErrorMessage={assemblyErrorMessage}
                emptyCaption="暂无分镜内容"
                density="compact"
                thumbClickMode="seek"
                onOpenShotGenerator={openShotGenerator}
                onRunAssembly={handleRunAssembly}
                showMergeFooter={false}
              />
            </>
          ) : isRegenerating ? (
            <div className="pcv-storyboard-regenerate-loading pcv-storyboard-regenerate-loading--expanded" data-testid="storyboard-shot-list-regenerating">
              <LoadingOutlined spin />
              <span>正在重新生成分镜…</span>
            </div>
          ) : (
            <div className="pcv-storyboard-empty pcv-storyboard-empty--compact" data-testid="storyboard-shot-list-empty">
              <span>暂无分镜</span>
            </div>
          )}

          {/* ⑤ Entity bindings (inherited from Script) + orphan refs */}
          <div className="pcv-brief-section" data-testid="storyboard-related-entities">
            <span className="pcv-brief-section-label">
              关联资产 <span className="pcv-brief-section-optional">（角色/产品及其参考关系）</span>
            </span>
            {entityBindings.length > 0 ? (
              <div className="pcv-storyboard-entity-list">
                {entityBindings.map((entity) => (
                  <EntityBindingCard key={entity.entity_id} entity={entity} workspaceId={workspaceId} />
                ))}
              </div>
            ) : (
              <div className="pcv-brief-asset-empty">暂无角色/产品实体</div>
            )}
            {looseAssets.length > 0 ? (
              <div className="pcv-brief-asset-row" style={{ marginTop: 8 }}>
                {looseAssets.map((a) => (
                  <SupportingAssetChip
                    key={a.asset_id || a.display_name}
                    asset={a}
                    workspaceId={workspaceId}
                    onRemove={() => removeSupportingAsset(a)}
                  />
                ))}
              </div>
            ) : null}
            <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={openSupportingAssetPicker}>
              <PlusOutlined /> 添加参考图
            </button>
          </div>

          {/* ⑥ Parameters — horizontal responsive grid */}
          <div className="pcv-storyboard-params-grid" data-testid="storyboard-params-grid">
            <div className="pcv-storyboard-param-cell">
              <div className="pcv-script-section-label">镜头数</div>
              <div className="pcv-brief-chip-row nodrag">
                {SHOT_COUNT_PRESETS.map((p) => (
                  <span
                    key={p.key}
                    className={`pcv-brief-chip${
                      p.key === 'AUTO'
                        ? shotCountMode === 'AUTO'
                          ? ' is-active'
                          : ''
                        : shotCountMode !== 'AUTO' && shotCount === ('value' in p ? p.value : null)
                          ? ' is-active'
                          : ''
                    }`}
                    onClick={() => pickShotCount(p.key, 'value' in p ? p.value : undefined)}
                  >
                    {p.label}
                  </span>
                ))}
                <span className={`pcv-brief-chip${isCustomShotActive ? ' is-active' : ''}`}>
                  <input
                    style={{ width: 36, border: 'none', background: 'none', color: 'inherit', outline: 'none', fontSize: 11 }}
                    placeholder="自定义"
                    value={customShotDraft}
                    onChange={(e) => setCustomShotDraft(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={commitCustomShotCount}
                    onKeyDown={(e) => e.key === 'Enter' && commitCustomShotCount()}
                  />
                </span>
              </div>
            </div>
            <div className="pcv-storyboard-param-cell">
              <div className="pcv-script-section-label">总时长</div>
              <div className="pcv-brief-chip-row nodrag">
                {DURATION_PRESETS.map((p) => (
                  <span
                    key={p.key}
                    className={`pcv-brief-chip${
                      p.key === 'AUTO'
                        ? durationMode === 'AUTO'
                          ? ' is-active'
                          : ''
                        : durationMode !== 'AUTO' && totalDurationSec === ('value' in p ? p.value : null)
                          ? ' is-active'
                          : ''
                    }`}
                    onClick={() => pickDuration(p.key, 'value' in p ? p.value : undefined)}
                  >
                    {p.label}
                  </span>
                ))}
                <span className={`pcv-brief-chip${isCustomDurationActive ? ' is-active' : ''}`}>
                  <input
                    style={{ width: 40, border: 'none', background: 'none', color: 'inherit', outline: 'none', fontSize: 11 }}
                    placeholder="自定义"
                    value={customDurationDraft}
                    onChange={(e) => setCustomDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={commitCustomDuration}
                    onKeyDown={(e) => e.key === 'Enter' && commitCustomDuration()}
                  />
                  秒
                </span>
              </div>
            </div>
            <div className="pcv-storyboard-param-cell">
              <span className="pcv-gen-field-label">风格</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={styleReference}
                options={STYLE_OPTIONS.map((v) => ({ value: v, label: v === 'AUTO' ? 'AUTO（AI）' : v }))}
                onChange={(v) => patchFields({ styleReference: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-storyboard-param-cell">
              <span className="pcv-gen-field-label">镜头语言</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={cameraLanguage}
                options={CAMERA_LANGUAGE_OPTIONS.map((v) => ({ value: v, label: v === 'AUTO' ? 'AUTO（AI）' : v }))}
                onChange={(v) => patchFields({ cameraLanguage: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
          </div>

          {/* Production Format Contract — not an AI prompt knob */}
          <div
            className={`pcv-storyboard-production-format${formatLocked || fields.finalVideoFormatMismatch ? ' is-locked' : ''}`}
            data-testid="storyboard-production-format"
          >
            <div className="pcv-storyboard-production-format-header">
              <span className="pcv-brief-section-label">成片规格</span>
              <span className="pcv-brief-section-optional">所有分镜统一使用该规格</span>
              {formatLocked || fields.finalVideoFormatMismatch ? (
                <span className="pcv-storyboard-production-format-warn" data-testid="storyboard-format-locked">
                  已有结果 · 修改将使旧视频失效
                </span>
              ) : null}
            </div>
            {!isProductionFormatComplete(productionFormat) ? (
              <div className="pcv-gen-capability-warning" data-testid="storyboard-format-required">
                需要选择成片规格
              </div>
            ) : (
              <div className="pcv-storyboard-production-format-summary" data-testid="storyboard-format-summary">
                {productionFormat!.aspect_ratio} · {productionFormat!.resolution}
                <span className="pcv-brief-section-optional"> · v{productionFormat!.version}</span>
              </div>
            )}
            <div className="pcv-storyboard-param-cell" style={{ marginTop: 8 }}>
              <span className="pcv-gen-field-label">
                长宽比 <span className="pcv-gen-field-required">*</span>
              </span>
              <div className="pcv-brief-chip-row nodrag" data-testid="storyboard-aspect-ratio-chips">
                {PRODUCTION_ASPECT_RATIO_OPTIONS.map((v) => (
                  <span
                    key={v}
                    className={`pcv-brief-chip${productionFormat?.aspect_ratio === v ? ' is-active' : ''}`}
                    onClick={() => applyFormatPatch({ aspect_ratio: v })}
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
            <div className="pcv-storyboard-param-cell" style={{ marginTop: 8 }}>
              <span className="pcv-gen-field-label">
                清晰度 <span className="pcv-gen-field-required">*</span>
              </span>
              <div className="pcv-brief-chip-row nodrag" data-testid="storyboard-resolution-chips">
                {PRODUCTION_RESOLUTION_OPTIONS.map((v) => (
                  <span
                    key={v}
                    className={`pcv-brief-chip${productionFormat?.resolution === v ? ' is-active' : ''}`}
                    onClick={() => applyFormatPatch({ resolution: v })}
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ⑦ Prompt */}
          <div className="pcv-brief-section pcv-storyboard-prompt-section">
            <span className="pcv-brief-section-label">补充说明（可选）</span>
            <textarea
              className="pcv-brief-textarea pcv-storyboard-prompt-textarea nodrag"
              placeholder="补充镜头节奏、视觉风格或转场要求（可选）"
              value={additionalInstructions}
              maxLength={ADDITIONAL_INSTRUCTIONS_MAX}
              rows={2}
              onChange={(e) => patchFields({ additionalInstructions: e.target.value })}
            />
            <span className="pcv-brief-textarea-counter">
              {additionalInstructions.length}/{ADDITIONAL_INSTRUCTIONS_MAX}
            </span>
          </div>
        </div>
      ) : null}

      {/* ⑦ Footer */}
      <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()}>
        <Tooltip title={providerSelectOptions.find((o) => o.value === resolvedProviderOptionId)?.title || 'Provider'} mouseEnterDelay={0.4}>
          <CanvasSelect
            size="small"
            className="pcv-gen-field-select pcv-gen-footer-select nodrag"
            popupClassName="pcv-gen-field-select-popup"
            value={resolvedProviderOptionId ?? undefined}
            disabled={providerOptionsError || providerSelectOptions.length === 0}
            placeholder={providerOptionsError ? '加载失败' : 'Provider'}
            options={providerSelectOptions}
            onChange={(v) => patchFields(buildExplicitProviderSelectionPatch(v))}
            popupMatchSelectWidth={false}
            placement="bottomLeft"
            getPopupContainer={() => document.body}
          />
        </Tooltip>
        <CanvasSelect
          size="small"
          className="pcv-gen-field-select pcv-gen-footer-select nodrag"
          popupClassName="pcv-gen-field-select-popup"
          value={skillSelectionSource === 'EXPLICIT' ? 'storyboard_generation' : 'AUTO'}
          options={[
            { value: 'AUTO', label: '自动（推荐）' },
            { value: 'storyboard_generation', label: '分镜生成' },
          ]}
          onChange={(v) =>
            patchFields(
              v === 'AUTO' ? { skillSelectionSource: 'AUTO', skillKey: null } : { skillSelectionSource: 'EXPLICIT', skillKey: v },
            )
          }
          popupMatchSelectWidth={false}
          placement="bottomLeft"
          getPopupContainer={() => document.body}
        />
        <Tooltip title="受控发散：基于当前内容，一次生成 3 个方向明显不同的分镜方案">
          <button
            type="button"
            className="pcv-gen-footer-diverge-btn"
            data-testid="storyboard-diverge-btn"
            disabled={!canDiverge}
            onClick={() => nodeData.onDivergeStoryboard?.(id)}
          >
            {isDiverging ? <LoadingOutlined spin /> : <ApartmentOutlined />}
          </button>
        </Tooltip>
        <Tooltip title={!canGenerate ? formatBlockingHint || (!scriptContext ? '请先关联剧本上下文' : undefined) : undefined}>
          <button
            type="button"
            className="pcv-gen-footer-generate-btn"
            data-testid="storyboard-generate-btn"
            disabled={!canGenerate}
            onClick={handleGenerateClick}
          >
            {isGenerating ? <LoadingOutlined spin /> : null}{' '}
            {isGenerating ? '生成中…' : visibleStoryboardResult || canRecoverSuperseded ? '重新生成分镜' : '生成分镜'}
          </button>
        </Tooltip>
        {visibleStoryboardResult ? (
          <Tooltip
            title={
              summary.allRequiredShotsReady
                ? '合并生成完整视频'
                : incompleteShotVideoCount > 0
                  ? `还有 ${incompleteShotVideoCount} 个分镜视频未生成`
                  : '请先为每个镜头生成视频'
            }
            mouseEnterDelay={0.3}
          >
            <button
              type="button"
              className="pcv-gen-footer-generate-btn"
              data-testid="storyboard-merge-video-btn"
              disabled={!summary.allRequiredShotsReady || isBusy || assemblyRunStatus === 'ASSEMBLING'}
              onClick={handleRunAssembly}
            >
              {assemblyRunStatus === 'ASSEMBLING' ? <LoadingOutlined spin /> : null} 合并生成视频
            </button>
          </Tooltip>
        ) : null}
        {visibleStoryboardResult && !summary.allRequiredShotsReady && incompleteShotVideoCount > 0 ? (
          <span className="pcv-gen-footer-hint" data-testid="storyboard-merge-incomplete-hint">
            还有 {incompleteShotVideoCount} 个分镜视频未生成
          </span>
        ) : null}
      </div>

      <Modal
        title="重新生成分镜"
        open={regenerateConfirmOpen}
        okText="重新生成"
        cancelText="取消"
        onOk={confirmRegenerate}
        onCancel={() => setRegenerateConfirmOpen(false)}
        destroyOnClose
        data-testid="storyboard-regenerate-confirm-modal"
      >
        <p>重新生成会创建一套新的分镜结构。现有分镜与已生成视频之间的关联将失效。</p>
        <Radio.Group
          value={regenerateMode}
          onChange={(e) => setRegenerateMode(e.target.value as StoryboardRegenerateMode)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}
        >
          <Radio value="keep_video_nodes" data-testid="storyboard-regenerate-option-keep">
            <div>
              <div>保留原视频节点，仅解除关联（推荐）</div>
              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
                原视频仍保留在画布上，可以继续查看和使用，但不会再属于新的分镜。
              </div>
            </div>
          </Radio>
          <Radio value="remove_canvas_nodes" data-testid="storyboard-regenerate-option-remove">
            <div>
              <div>清除旧分镜及相关视频节点</div>
              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
                旧分镜和相关视频节点会从当前画布移除。已经生成的图片和视频资产不会删除，仍可在资产库中找到。
              </div>
            </div>
          </Radio>
        </Radio.Group>
      </Modal>
    </div>
  )
}

export default memo(StoryboardGenerationNodeImpl)
